/** CSS contract: css-library/components/01-leaves/select.css. */
import type { Ref } from 'react';
import { useSelectPopover } from '../lib/useSelectPopover';
import { ButtonControl } from './ButtonControl';

import { useId } from 'react';
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';

export interface ComboBoxProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'defaultValue' | 'onChange' | 'type' | 'list' | 'role'
  > {
  options: string[];
  value: string;

  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  placeholder?: string;
}

export function ComboBox(
  { ref, options,
    value,
    onChange,
    onCommit,
    placeholder,
    className,
    id,
    onFocus,
    onKeyDown,
    onBlur,
    ...rest
  }: ComboBoxProps & { ref?: Ref<HTMLInputElement> },
) {
  const query = value.trim().toLowerCase();
  const filtered = query ? options.filter((option) => option.toLowerCase().includes(query)) : options;
  const { open, setOpen, entered, container, wrapRef, triggerRef, popoverRef, style } =
    useSelectPopover<HTMLInputElement>('combobox', filtered.length);
  const listboxId = useId();
  function commitTyped() {
    const trimmed = value.trim();
    if (trimmed) onCommit(trimmed);
  }
  function pick(name: string) {
    onChange(name);
    setOpen(false);
    onCommit(name);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
    setOpen(true);
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    onFocus?.(e);
    if (e.defaultPrevented) return;
    setOpen(true);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      setOpen(false);
      commitTyped();
    }
  }

  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    onBlur?.(e);
    if (e.defaultPrevented) return;
    setOpen(false);
    commitTyped();
  }

  return (
    <div ref={wrapRef} className={cx('select-wrap', open && 'is-open', className)}>
      <input
        ref={mergeRefs(triggerRef, ref)}
        type="text"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        className="select-display combobox-input"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        {...rest}
      />

      {open &&
        filtered.length > 0 &&
        createPortal(
          <div
            ref={popoverRef}
            id={listboxId}
            role="listbox"
            className={cx('select-popover', entered && 'is-open')}
            style={style}
          >
            {filtered.map((name) => (
              <ButtonControl
                key={name}
                type="button"
                role="option"
                aria-selected={name === value}
                className="select-option"

                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(name)}
              >
                {name}
              </ButtonControl>
            ))}
          </div>,
          container ?? document.body,
        )}
    </div>
  );
}
